import React, { useState, useEffect, useCallback, useMemo } from "react";
import { withRouter, useHistory } from "react-router-dom";
import moment from "moment";

import { withStyles } from "@material-ui/core/styles";
import { Typography, Fade, Input, IconButton } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import bigInt from "big-integer";

import { FadeInWhenVisible } from "../animation";

import { withNamespaces } from "react-i18next";
import { VOTE, toFixed, tokens } from "../../constants/constants";

import { ReactComponent as ThumbupIcon } from "../../assets/thumb-up-green.svg";
import { ReactComponent as ThumbdownIcon } from "../../assets/thumb-down-red.svg";

import VoteModal from "./components/voteModal";

import Store from "../../stores/store";
import LogoImg from "../pools/components/LogoImg";
import Spinner from "../stake/components/Spinner";
import PoolCard from "../poolCard/poolCard";
import { sortPools } from "../../utils/sortPools";

const styles = (theme) => ({
  root: {
    flex: 1,
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "flex-start",
    flexDirection: "column",
    padding: "30px",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      padding: "70px",
    },
    fontFamily: "Poppins",
    position: "relative",
  },

  mainContainer: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: "100%",
    maxWidth: "1140px",
  },
  header: {
    fontFamily: "Poppins",
    fontSize: "30px",
    fontWeight: "600",
    lineHeight: "36px",
    textAlign: "left",
    letterSpacing: "0px",
    color: theme?.colors?.home?.text,
    marginBottom: "10px",
    [theme.breakpoints.up("sm")]: {
      marginBottom: "20px",
    },
  },

  searchBox: {
    width: "100%",
    height: "40px",
    borderRadius: "24px",
    border: "solid 1px " + theme?.colors?.pools?.border,
  },
  searchInput: {
    fontSize: "15px",
    lineHeight: "18px",
    letterSpacing: "0px",
    color: theme?.colors?.pools?.searchText,
    padding: "0px",
    minWidth: "180px",
  },

  searchMagnifier: {
    width: "17px",
    height: "17px",
    margin: "16px",
    color: theme?.colors?.pools?.searchMagnifier,
  },
  mainBody: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  poolsContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "30px",
    [theme.breakpoints.up("sm")]: {
      marginBottom: "75px",
    },
  },
  poolsWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",

    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridColumnGap: "30px",
      gridRowGap: "40px",
    },

    [theme.breakpoints.up("lg")]: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gridColumnGap: "30px",
      gridRowGap: "40px",
    },
  },
  poolItemWrapper: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  text: {
    fontSize: "16px",
    fontWeight: "lighter",
    textAlign: "left",
    color: theme?.colors?.voting?.text,
    lineHeight: "24px",
    marginBottom: "15px",
    marginTop: "0px",
    [theme.breakpoints.up("md")]: {
      fontSize: "20px",
    },
  },

  priceText: {
    color: theme?.colors?.voting?.secondaryText,
  },

  emptyContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "20px",
  },
  warningContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    height: "50px",
    padding: "10px 20px",
    backgroundColor: theme?.colors?.wallet?.buttonBackground,
    borderRadius: "12px",
  },
  warningText: {
    color: theme?.colors?.wallet?.buttonTextSecondary,
    textAlign: "center",
    fontSize: "20px",
    lineHeight: "25px",
  },
  poolsTableContainer: {
    border: "solid 1px " + theme?.colors?.voting?.tableBorder,
    borderRadius: "14px",
  },
  poolsTable: {
    minWidth: "768px",
  },
  logoTitleContainer: {
    display: "flex",
    alignItems: "center",
  },
  logoImage: {
    width: "32px",
    height: "32px",
    marginRight: "20px",
  },

  voteActionContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },

  voteActionItem: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
  },

  table: {
    width: "100%",
    marginTop: "1.5rem",
    borderCollapse: "collapse",
    borderSpacing: "0",
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,
    borderRadius: "32px",

    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "table",
    },
  },

  mobileContainer: {
    width: "100%",
    marginTop: "1.5rem",
    gap: "1.5rem",

    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  tableHeader: {
    color: "#A0A0A0",
    fontSize: "14px",
    // background: "#393939",
    background: theme?.colors?.pools?.headerBackground,
    borderBottom: "1px solid " + theme?.colors?.pools?.rowBorderBottom,
    padding: "16px",
  },
  tableHeaderItem: {
    width: "15%",
    padding: "16px",
    fontWeight: "normal",
  },
  tableBody: {
    // background: "#242424",
    background: theme?.colors?.pools?.bodyBackground,
    color: "#fff",
    width: "100%",
  },
  tableRow: {
    // borderBottom: "1px solid #393939",
    borderBottom: "1px solid" + theme?.colors?.pools?.rowBorderBottom,
    cursor: "pointer",
    color: theme?.colors?.pools?.tableDataText,
  },
  tableRowLast: {
    cursor: "pointer",
    borderBottom: "none",
    color: theme?.colors?.pools?.tableDataText,
  },
  tableNameRow: {
    display: "flex",
    alignItems: "center",
    color: theme?.colors?.pools?.tableDataText,
    fontWeight: 600,
  },
  tableData: {
    padding: "16px",
    color: theme?.colors?.pools?.tableDataText,
  },
  button: {
    padding: "14px 20px",
    fontSize: "16px",
    margin: "10px",
    cursor: "pointer",

    borderRadius: "20px",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
    backgroundColor: "transparent",
    border: "solid 2px" + theme?.colors?.wallet?.buttonBorderSecondary,
    color: theme?.colors?.wallet?.buttonTextTertiary,
  },
  disabledButton: {
    padding: "14px 20px",
    fontSize: "16px",
    margin: "10px",
    cursor: "initial",
    opacity: "0.4",

    borderRadius: "20px",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
    backgroundColor: "transparent",
    border: "solid 2px" + theme?.colors?.wallet?.buttonBorderSecondary,
    color: theme?.colors?.wallet?.buttonTextTertiary,
  },
  loader: {
    width: "100%",
    display: "flex",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "62px",
  },
});

const dispatcher = Store.dispatcher;
const store = Store.store;

const Voting = ({ classes, location }) => {
  const history = useHistory();
  const [votingPools, setVotingPools] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [filteredPools, setFilteredPools] = useState([]);
  const [isVoteModalShown, showVoteModal] = useState(false);
  const [voteModalTitle, setVoteModalTitle] = useState("");
  const [votePool, setVotePool] = useState({});
  const [currentTimestamp, setCurrentTimestamp] = useState(moment.now() / 1000);

  const itemsPerPage = 10;
  const [page, setPage] = useState(1);

  const maxPage = useMemo(
    () => Math.ceil(filteredPools.length / itemsPerPage),
    [filteredPools]
  );

  useEffect(() => {
    const queryPage =
      Number(new URLSearchParams(location.search).get("page")) <= 0
        ? 1
        : Number(new URLSearchParams(location.search).get("page"));

    if (maxPage >= queryPage) {
      setPage(queryPage);

      const params = new URLSearchParams();
      params.append("page", queryPage);

      history.push({ search: params.toString() });
    } else {
      setPage(1);

      const params = new URLSearchParams();
      params.append("page", 1);

      history.push({ search: params.toString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxPage, location.search]);

  const handlePrev = () => {
    if (page > 1) {
      setPage(page - 1);

      const params = new URLSearchParams();
      params.append("page", page - 1);

      history.push({ search: params.toString() });
    }
  };

  const handleNext = () => {
    if (page < maxPage) {
      setPage(page + 1);
      const params = new URLSearchParams();
      params.append("page", page + 1);

      history.push({ search: params.toString() });
    }
  };

  const paginatedData = useMemo(
    () => filteredPools.slice((page - 1) * itemsPerPage, page * itemsPerPage),
    [page, filteredPools]
  );

  const isInVoting = (pool) => {
    if (pool.certified) {
      return false;
    }
    const currentTimestamp = Math.floor(Date.now() / 1000);

    return (
      !pool.presaleCancelled &&
      pool.openTime > currentTimestamp &&
      pool.presaleType !== "2"
    );
  };

  const getVotingPresales = useCallback(() => {
    const publicPresales = store.getStore("publicPresales");
    const filteredVotingPresales = Object.values(publicPresales).filter(
      (presale) => presale?.visible && isInVoting(presale)
    );

    return sortPools(filteredVotingPresales);
  }, []);

  const getRemainingTime = (remainingTime) => {
    const remainingDay = Math.floor(remainingTime / 3600 / 24);
    const remainingHours = Math.floor(remainingTime / 3600);
    const remainingMinutes = Math.floor(remainingTime / 60);
    const remainingSeconds = Math.floor(remainingTime);

    if (remainingDay > 0) {
      return `${remainingDay} days`;
    }
    const _remainingHours =
      remainingHours < 10 ? `0${remainingHours}` : `${remainingHours}`;
    const _remainingMins =
      remainingMinutes % 60 < 10
        ? `0${remainingMinutes % 60}`
        : `${remainingMinutes % 60}`;
    const _remainingSecs =
      remainingSeconds % 60 < 10
        ? `0${remainingSeconds % 60}`
        : `${remainingSeconds % 60}`;
    return `${_remainingHours}:${_remainingMins}:${_remainingSecs}`;
  };

  const getPoolsSummaryReturned = useCallback(() => {
    const votingPresales = getVotingPresales();
    setVotingPools(votingPresales);
    setLoaded(true);
  }, [getVotingPresales]);

  useEffect(() => {
    getPoolsSummaryReturned();
    let interval = setInterval(() => {
      setCurrentTimestamp(moment.now() / 1000);
    }, 1000);

    const watchPoolsChangeId = setInterval(() => {
      getPoolsSummaryReturned();
    }, 1000);

    return () => {
      clearInterval(watchPoolsChangeId);
      clearInterval(interval);
    };
  }, [getPoolsSummaryReturned]);

  useEffect(() => {
    if (!searchString || searchString === "") {
      setFilteredPools(votingPools);
      return;
    }
    const _searchString = searchString?.toLowerCase();
    const _filtered = votingPools.filter((pool) => {
      const hardCap = parseFloat(
        toFixed(bigInt(pool.hardCapInWei), pool?.fundingTokenDecimals || 18, 2)
      );
      const softCap = parseFloat(
        toFixed(bigInt(pool.softCapInWei), pool?.fundingTokenDecimals || 18, 2)
      );
      const tokenPrice = parseFloat(
        toFixed(
          bigInt(pool.tokenPriceInWei),
          pool?.fundingTokenDecimals || 18,
          6
        )
      );
      const yesVotes = parseFloat(toFixed(bigInt(pool.yesVotes), 18, 4));
      const yesPercentage = parseFloat((yesVotes / 1000000) * 100).toFixed(2);
      const noVotes = parseFloat(toFixed(bigInt(pool.noVotes), 18, 4));
      const noPercentage = parseFloat((noVotes / 1000000) * 100).toFixed(2);
      const remainingTime = getRemainingTime(pool.openTime - currentTimestamp);
      const fundingToken = tokens[pool.fundingTokenAddress];

      return (
        pool?.saleTitle?.toLowerCase().trim().includes(_searchString) ||
        pool?.tokenAddress?.toLowerCase().trim().includes(_searchString) ||
        `${tokenPrice} ${fundingToken?.label || "ETH"}`.includes(
          searchString?.toLowerCase()
        ) ||
        `${softCap} ${fundingToken?.label || "ETH"}`.includes(
          searchString?.toLowerCase()
        ) ||
        `${hardCap} ${fundingToken?.label || "ETH"}`.includes(
          searchString?.toLowerCase()
        ) ||
        remainingTime.includes(searchString?.toLowerCase()) ||
        `${yesPercentage}%`.includes(searchString?.toLowerCase()) ||
        `${noPercentage}%`.includes(searchString?.toLowerCase())
      );
    });

    setFilteredPools(_filtered);
  }, [votingPools, currentTimestamp, searchString]);

  const onVote = (pool, yesOrNo) => {
    // if (yesOrNo) {
    setVotePool(pool);
    setVoteModalTitle(pool.saleTitle);
    showVoteModal(true);
    // } else {
    //   dispatcher.dispatch({
    //     type: VOTE,
    //     content: {
    //       presaleIndex: pool.presaleIndex,
    //       voteType: yesOrNo,
    //     },
    //   });
    // }
  };

  const onVoteCommit = (voteChoice) => {
    dispatcher.dispatch({
      type: VOTE,
      content: {
        presaleIndex: votePool.presaleIndex,
        voteType: voteChoice,
      },
    });
  };

  return (
    <div className={classes.root}>
      <Fade in={true} timeout={{ enter: 1000 }}>
        <div className={classes.mainContainer}>
          <Typography className={classes.header} variant="h1">
            Project Research
          </Typography>
          <Input
            classes={{ root: classes.searchBox, input: classes.searchInput }}
            disableUnderline={true}
            placeholder="Search by Name, Address..."
            value={searchString}
            startAdornment={
              <SearchIcon classes={{ root: classes.searchMagnifier }} />
            }
            onChange={(ev) => {
              setSearchString(ev.target.value);
            }}
          />
          <div className={classes.mainBody}>
            <div className={classes.poolsContainer}>
              {!loaded ? (
                <div className={classes.loader}>
                  <Spinner strokeSize="6px" width="54px" height="54px" />
                </div>
              ) : votingPools.length === 0 || filteredPools.length === 0 ? (
                <div className={classes.emptyContainer}>
                  <div className={classes.warningContainer}>
                    <Typography variant="h4" className={classes.warningText}>
                      No research projects were found
                    </Typography>
                  </div>
                </div>
              ) : (
                <FadeInWhenVisible duration={0.83} delay={0.1}>
                  <div>
                    <table className={classes.table}>
                      <thead className={classes.tableHeader}>
                        <tr>
                          <th
                            style={{
                              width: "22%",
                              padding: "16px",
                              borderTopLeftRadius: "32px",
                              fontWeight: "normal",
                            }}
                          >
                            Name
                          </th>
                          <th
                            className={classes.tableHeaderItem}
                            style={{
                              width: "18%",
                            }}
                          >
                            Price
                          </th>
                          <th className={classes.tableHeaderItem}>Softcap</th>
                          <th className={classes.tableHeaderItem}>Hardcap</th>
                          <th className={classes.tableHeaderItem}>Opens in</th>
                          <th
                            style={{
                              width: "15%",
                              padding: "16px",
                              borderTopRightRadius: "32px",
                              fontWeight: "normal",
                            }}
                          >
                            Voting
                          </th>
                        </tr>
                      </thead>
                      <tbody className={classes.tableBody}>
                        {filteredPools
                          .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                          .map((pool, index) => {
                            const hardCap = parseFloat(
                              toFixed(
                                bigInt(pool?.hardCapInWei) || "0",
                                pool?.fundingTokenDecimals || 18,
                                6
                              )
                            );
                            const softCap = parseFloat(
                              toFixed(
                                bigInt(pool.softCapInWei) || "0",
                                pool?.fundingTokenDecimals || 18,
                                6
                              )
                            );
                            const tokenPrice = parseFloat(
                              toFixed(
                                bigInt(pool.tokenPriceInWei) || "0",
                                pool?.fundingTokenDecimals || 18,
                                6
                              )
                            );
                            const yesVotes = parseFloat(
                              toFixed(bigInt(pool.yesVotes), 18, 6)
                            );
                            const yesPercentage = parseFloat(
                              (yesVotes / 1000000) * 100
                            ).toFixed(2);
                            const noVotes = parseFloat(
                              toFixed(bigInt(pool.noVotes), 18, 6)
                            );
                            const noPercentage = parseFloat(
                              (noVotes / 1000000) * 100
                            ).toFixed(2);
                            const remainingTime = getRemainingTime(
                              pool.openTime - currentTimestamp
                            );
                            const fundingToken =
                              tokens[pool.fundingTokenAddress];

                            return (
                              <tr
                                key={pool.saleTitle}
                                onClick={() => {
                                  history.push(pool.url);
                                }}
                                className={
                                  index === paginatedData.length - 1
                                    ? classes.tableRowLast
                                    : classes.tableRow
                                }
                              >
                                <td
                                  style={
                                    index === paginatedData.length - 1
                                      ? {
                                          padding: "16px",
                                          borderBottomLeftRadius: "32px",
                                        }
                                      : { padding: "16px" }
                                  }
                                >
                                  <div className={classes.tableNameRow}>
                                    <LogoImg logoImage={pool.linkLogo} />
                                    {pool.saleTitle}

                                    {/* todo: certified icon */}
                                    {/* <span>🔰</span> */}
                                  </div>
                                </td>
                                <td className={classes.tableData}>
                                  {tokenPrice} {fundingToken?.label || "ETH"}
                                </td>
                                <td className={classes.tableData}>
                                  {softCap} {fundingToken?.label || "ETH"}
                                </td>
                                <td className={classes.tableData}>
                                  {hardCap} {fundingToken?.label || "ETH"}
                                </td>
                                <td className={classes.tableData}>
                                  {remainingTime}
                                </td>
                                <td
                                  style={
                                    index === paginatedData.length - 1
                                      ? {
                                          padding: "16px",
                                          borderBottomRightRadius: "32px",
                                        }
                                      : { padding: "16px" }
                                  }
                                >
                                  <div className={classes.voteActionContainer}>
                                    <div className={classes.voteActionItem}>
                                      <Typography
                                        className={classes.percentText}
                                      >
                                        {yesPercentage}%
                                      </Typography>
                                      <IconButton
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          onVote(pool, true);
                                        }}
                                      >
                                        <ThumbupIcon />
                                      </IconButton>
                                    </div>
                                    <div className={classes.voteActionItem}>
                                      <Typography
                                        className={classes.percentText}
                                      >
                                        {noPercentage}%
                                      </Typography>
                                      <IconButton
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          onVote(pool, false);
                                        }}
                                      >
                                        <ThumbdownIcon />
                                      </IconButton>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>

                    <div className={classes.mobileContainer}>
                      {filteredPools
                        .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                        .map((item) => (
                          <PoolCard key={item.tokenAddress} pool={item} />
                        ))}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      className={
                        page === 1 ? classes.disabledButton : classes.button
                      }
                      onClick={handlePrev}
                    >{`< ${page * itemsPerPage - itemsPerPage} - ${
                      page * itemsPerPage
                    }`}</button>
                    <button
                      className={
                        page === maxPage
                          ? classes.disabledButton
                          : classes.button
                      }
                      onClick={handleNext}
                    >{`${page * itemsPerPage + 1} - ${
                      page * itemsPerPage + itemsPerPage
                    } >`}</button>
                  </div>
                </FadeInWhenVisible>
              )}
            </div>
          </div>
        </div>
      </Fade>
      <VoteModal
        closeModal={() => {
          showVoteModal(false);
        }}
        modalOpen={isVoteModalShown}
        salesTitle={voteModalTitle || ""}
        onVote={onVoteCommit}
      />
    </div>
  );
};

export default withNamespaces()(withRouter(withStyles(styles)(Voting)));
